import "./address-suggest-box.css";

import { LatLng } from "../../../services/maps/map-provider.interface";
import { AddressSearchMode, AddressSuggestion, GeoAddress } from "../../../services/geo-search/address-search-provider.interface";
import { GeoSearchChain } from "../../../services/geo-search/geo-search-chain";
import { isAbortError } from "../../../services/geo-search/geo-search-fetch";
import { SuggestionsAnnouncer } from "../../../utils/suggestions-announcer";

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 3;
const DEFAULT_SUGGESTION_LIMIT = 5;

export type AddressSuggestBoxOptions = {
    input: HTMLInputElement,
    chain: GeoSearchChain,
    mode: AddressSearchMode,
    getBiasLocation: () => LatLng | null,
    getCountryCodes: () => string[],
    getAnnouncement: () => string,
    onAddressSelected: (address: GeoAddress) => void | Promise<void>,
    limit?: number
};

let nextListboxId = 0;

/**
 * The suggestion list every provider shares. Providers only return data, so the markup,
 * keyboard handling and announcements are identical whichever one answered.
 */
export class AddressSuggestBox {
    private readonly _options: AddressSuggestBoxOptions;
    private readonly _input: HTMLInputElement;
    private readonly _listbox: HTMLUListElement;
    private readonly _announcer: SuggestionsAnnouncer;

    private _suggestions: AddressSuggestion[] = [];
    private _activeIndex = -1;
    private _debounceTimer: number | null = null;
    private _abortController: AbortController | null = null;
    private _blurTimer: number | null = null;

    constructor(options: AddressSuggestBoxOptions) {
        this._options = options;
        this._input = options.input;
        this._announcer = new SuggestionsAnnouncer(options.getAnnouncement);
        this._listbox = this._createListbox();

        this._addEventListeners();
    }

    public dispose(): void {
        this._cancelPendingWork();
        this._removeEventListeners();
        this._listbox.remove();
    }

    private _createListbox(): HTMLUListElement {
        const listbox = document.createElement("ul");
        listbox.id = `ul_address_suggest_box_${nextListboxId++}`;
        listbox.className = "address-suggest-box";
        listbox.setAttribute("role", "listbox");
        listbox.hidden = true;

        // A <dialog> paints in the browser's top layer, so a list parented to the body would sit
        // behind it whatever the z-index. Positioning is viewport-relative either way.
        const host = this._input.closest("dialog") ?? document.body;
        host.appendChild(listbox);

        this._input.setAttribute("aria-controls", listbox.id);
        this._input.setAttribute("aria-expanded", "false");

        return listbox;
    }

    private _addEventListeners(): void {
        this._input.addEventListener("input", this._handleInput);
        this._input.addEventListener("keydown", this._handleKeyDown);
        this._input.addEventListener("focus", this._handleFocus);
        this._input.addEventListener("blur", this._handleBlur);
        document.addEventListener("mousedown", this._handleDocumentMouseDown);
        window.addEventListener("resize", this._handleReposition);
        window.addEventListener("scroll", this._handleReposition, true);
    }

    private _removeEventListeners(): void {
        this._input.removeEventListener("input", this._handleInput);
        this._input.removeEventListener("keydown", this._handleKeyDown);
        this._input.removeEventListener("focus", this._handleFocus);
        this._input.removeEventListener("blur", this._handleBlur);
        document.removeEventListener("mousedown", this._handleDocumentMouseDown);
        window.removeEventListener("resize", this._handleReposition);
        window.removeEventListener("scroll", this._handleReposition, true);
    }

    private readonly _handleInput = (): void => {
        this._scheduleSearch(this._input.value);
    };

    private readonly _handleFocus = (): void => {
        if (this._suggestions.length > 0)
            this._openList();
    };

    private readonly _handleBlur = (): void => {
        this._blurTimer = window.setTimeout(() => this._closeList(), 150);
    };

    private readonly _handleDocumentMouseDown = (event: MouseEvent): void => {
        const target = event.target as Node | null;
        if (target && (this._listbox.contains(target) || this._input.contains(target)))
            return;

        this._closeList();
    };

    private readonly _handleReposition = (): void => {
        if (!this._listbox.hidden)
            this._positionList();
    };

    private readonly _handleKeyDown = (event: KeyboardEvent): void => {
        if (this._listbox.hidden || this._suggestions.length === 0)
            return;

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                this._setActiveIndex(this._activeIndex + 1);
                return;
            case "ArrowUp":
                event.preventDefault();
                this._setActiveIndex(this._activeIndex - 1);
                return;
            case "Enter":
                if (this._activeIndex < 0)
                    return;
                event.preventDefault();
                void this._selectSuggestionAsync(this._suggestions[this._activeIndex]);
                return;
            case "Escape":
                this._closeList();
                return;
        }
    };

    private _scheduleSearch(query: string): void {
        this._cancelPendingWork();

        this._debounceTimer = window.setTimeout(() => void this._searchAsync(query.trim()), DEBOUNCE_MS);
    }

    private async _searchAsync(query: string): Promise<void> {
        if (query.length < MIN_QUERY_LENGTH) {
            this._renderSuggestions([]);
            return;
        }

        this._abortController = new AbortController();

        try {
            const suggestions = await this._options.chain.suggestAsync(
                query,
                {
                    mode: this._options.mode,
                    limit: this._options.limit ?? DEFAULT_SUGGESTION_LIMIT,
                    at: this._options.getBiasLocation() ?? undefined,
                    countryCodes: this._options.getCountryCodes()
                },
                this._abortController.signal
            );

            this._renderSuggestions(suggestions);
        }
        catch (error) {
            if (isAbortError(error))
                return;

            // Every provider in the chain failed; leaving the last list up would be misleading.
            console.error("Address suggestions failed", error);
            this._renderSuggestions([]);
        }
    }

    private _cancelPendingWork(): void {
        if (this._debounceTimer !== null) {
            window.clearTimeout(this._debounceTimer);
            this._debounceTimer = null;
        }

        if (this._blurTimer !== null) {
            window.clearTimeout(this._blurTimer);
            this._blurTimer = null;
        }

        if (this._abortController) {
            this._abortController.abort();
            this._abortController = null;
        }
    }

    private _renderSuggestions(suggestions: AddressSuggestion[]): void {
        this._suggestions = suggestions;
        this._activeIndex = -1;
        this._listbox.innerHTML = "";

        this._announcer.update(suggestions.length > 0);

        if (suggestions.length === 0) {
            this._closeList();
            return;
        }

        suggestions.forEach((suggestion, index) => {
            this._listbox.appendChild(this._createOption(suggestion, index));
        });

        this._openList();
    }

    private _createOption(suggestion: AddressSuggestion, index: number): HTMLLIElement {
        const option = document.createElement("li");
        option.id = `${this._listbox.id}_option_${index}`;
        option.className = "address-suggest-box-option";
        option.setAttribute("role", "option");
        option.setAttribute("aria-selected", "false");
        option.textContent = suggestion.label;

        // mousedown fires before blur, so the list is still there when the click lands
        option.addEventListener("mousedown", event => {
            event.preventDefault();
            void this._selectSuggestionAsync(suggestion);
        });

        return option;
    }

    private _setActiveIndex(index: number): void {
        const lastIndex = this._suggestions.length - 1;
        const options = Array.from(this._listbox.children) as HTMLLIElement[];

        if (this._activeIndex >= 0)
            this._markOption(options[this._activeIndex], false);

        if (index < 0)
            this._activeIndex = lastIndex;
        else if (index > lastIndex)
            this._activeIndex = 0;
        else
            this._activeIndex = index;

        const activeOption = options[this._activeIndex];
        this._markOption(activeOption, true);
        activeOption?.scrollIntoView({ block: "nearest" });
        this._input.setAttribute("aria-activedescendant", activeOption?.id ?? "");
    }

    private _markOption(option: HTMLLIElement | undefined, isActive: boolean): void {
        if (!option)
            return;

        option.classList.toggle("active", isActive);
        option.setAttribute("aria-selected", String(isActive));
    }

    private async _selectSuggestionAsync(suggestion: AddressSuggestion): Promise<void> {
        this._input.value = suggestion.label;
        this._closeList();

        const address = await this._options.chain.resolveAsync(suggestion);
        if (!address)
            return;

        await this._options.onAddressSelected({
            ...address,
            label: address.label ?? suggestion.label
        });
    }

    private _openList(): void {
        // shown first so positioning can measure it; a hidden element has no box to correct against
        this._listbox.hidden = false;
        this._positionList();
        this._input.setAttribute("aria-expanded", "true");
    }

    private _closeList(): void {
        this._listbox.hidden = true;
        this._activeIndex = -1;
        this._input.setAttribute("aria-expanded", "false");
        this._input.removeAttribute("aria-activedescendant");
    }

    private _positionList(): void {
        const bounds = this._input.getBoundingClientRect();

        this._listbox.style.left = `${bounds.left}px`;
        this._listbox.style.top = `${bounds.bottom}px`;
        this._listbox.style.width = `${bounds.width}px`;

        // A transformed ancestor becomes the containing block for position:fixed — the store
        // selector dialog centres itself with one — so the viewport coordinates above land
        // offset. Correct by however far the list actually ended up from the input.
        const placed = this._listbox.getBoundingClientRect();

        // nothing to measure against when the list has no box yet, and correcting from a zero
        // rect would throw it across the page
        if (placed.width === 0)
            return;

        const offsetX = placed.left - bounds.left;
        const offsetY = placed.top - bounds.bottom;

        if (offsetX === 0 && offsetY === 0)
            return;

        this._listbox.style.left = `${bounds.left - offsetX}px`;
        this._listbox.style.top = `${bounds.bottom - offsetY}px`;
    }
}
